import type { Grade } from '@fishymeter/types';
import { cn } from '@/lib/utils';

const GRADE_STROKE: Record<Grade, string> = {
  A: 'stroke-grade-a',
  B: 'stroke-grade-b',
  C: 'stroke-grade-c',
  D: 'stroke-grade-d',
  F: 'stroke-grade-f',
};

const GRADE_TEXT: Record<Grade, string> = {
  A: 'text-grade-a',
  B: 'text-grade-b',
  C: 'text-grade-c',
  D: 'text-grade-d',
  F: 'text-grade-f',
};

const SIZE: Record<'sm' | 'md' | 'lg', { box: number; stroke: number; font: string }> = {
  sm: { box: 80, stroke: 8, font: 'text-2xl' },
  md: { box: 120, stroke: 10, font: 'text-4xl' },
  lg: { box: 180, stroke: 12, font: 'text-6xl' },
};

export interface ScoreGaugeProps {
  score: number;
  grade: Grade;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ScoreGauge({ score, grade, size = 'lg', className }: ScoreGaugeProps) {
  const { box, stroke, font } = SIZE[size];
  const radius = (box - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg width={box} height={box} aria-label={`Score ${score} out of 100, grade ${grade}`}>
        <circle
          cx={box / 2}
          cy={box / 2}
          r={radius}
          fill="none"
          className="stroke-muted"
          strokeWidth={stroke}
        />
        <circle
          cx={box / 2}
          cy={box / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={cn('transition-[stroke-dashoffset] duration-1000', GRADE_STROKE[grade])}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${box / 2} ${box / 2})`}
        />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center font-bold', font, GRADE_TEXT[grade])}>
        {score}
      </div>
    </div>
  );
}
